import { PushMaterial, Material, Vector3, MaterialDefines, VertexBuffer, MaterialHelper, EffectFallbacks, InstancedMesh, Constants, Vector4, Vector2, } from "@babylonjs/core";
import { Size3 } from "core/geometry";
import { ClipIndex, IsHolographicBox, IsHolographicCylinder, IsHolographicSphere } from "../display";
import { Texture3 } from "./textures";
import { NeighborsAddress, TargetProxy, Tile, TileAddress } from "core/tiles";
import { ElevationLayerView, TextureLayerView } from "../map";
import { Range } from "core/math";
import { ElevationHelpers } from "core/dem";
export var TileBorder;
(function (TileBorder) {
    TileBorder[TileBorder["None"] = 0] = "None";
    TileBorder[TileBorder["East"] = 1] = "East";
    TileBorder[TileBorder["South"] = 2] = "South";
    TileBorder[TileBorder["SouthEast"] = 3] = "SouthEast";
})(TileBorder || (TileBorder = {}));
class TileLayout {
    constructor(tile, layer, area = null) {
        this.tile = tile;
        this.layer = layer;
        this.area = area;
    }
    dispose() {
        this.area?.release();
    }
}
class TextureLayout extends TileLayout {
    constructor(tile, layer, area = null) {
        super(tile, layer, area);
    }
}
class ElevationLayout extends TileLayout {
    constructor(tile, layer, area = null, normalArea = null) {
        super(tile, layer, area);
        this.normalArea = normalArea;
        this._neighbors = TileAddress.ToNeighborsXY(tile.address);
    }
    get neighbors() {
        return this._neighbors;
    }
    dispose() {
        super.dispose();
        this.normalArea?.release();
    }
}
export class Map3dMaterial extends PushMaterial {
    constructor(name, scene, shaderName) {
        super(name, scene);
        this._shaderName = null;
        this._holoBounds = null;
        this._clipPlanesAddedObservers = null;
        this._clipPlanesRemovedObservers = null;
        this._textureSampler = null;
        this._elevationSampler = null;
        this._elevationRange = null;
        this._mapScale = Vector3.One();
        this._textureTileLayouts = new Map();
        this._elevationTileLayouts = new Map();
        this._displayResolution = Size3.Zero();
        this._shaderName = shaderName ?? Map3dMaterial.ShaderName;
        this._imagesTarget = new TargetProxy(this._imagesAdded.bind(this), this._imagesRemoved.bind(this), this._imagesUpdated.bind(this));
        this._elevationsTarget = new TargetProxy(this._elevationsAdded.bind(this), this._elevationsRemoved.bind(this), this._elevationsUpdated.bind(this));
    }
    get imagesTarget() {
        return this._imagesTarget;
    }
    get elevationsTarget() {
        return this._elevationsTarget;
    }
    getClassName() {
        return Map3dMaterial.ClassName;
    }
    get holographicBounds() {
        return this._holoBounds;
    }
    set holographicBounds(value) {
        if (this._holoBounds !== value) {
            this._unbindHolographicBounds();
            this._holoBounds = value;
            this._bindHolographicBounds();
            this.markAsDirty(Material.AttributesDirtyFlag);
        }
    }
    get displayResolution() {
        return this._displayResolution;
    }
    set displayResolution(value) {
        if (this._displayResolution !== value) {
            this._displayResolution = value;
            this.markAsDirty(Material.AttributesDirtyFlag);
        }
    }
    get mapScale() {
        return this._mapScale;
    }
    set mapScale(value) {
        if (this._mapScale !== value) {
            this._mapScale = value;
            this.markAsDirty(Material.AttributesDirtyFlag);
        }
    }
    isReadyForSubMesh(mesh, subMesh, useInstances) {
        const drawWrapper = subMesh._drawWrapper;
        if (this.isFrozen) {
            if (drawWrapper.effect && drawWrapper._wasPreviouslyReady && drawWrapper._wasPreviouslyUsingInstances === useInstances) {
                return true;
            }
        }
        const defines = new MaterialDefines();
        const scene = this.getScene();
        if (this._isReadyForSubMesh(subMesh)) {
            return true;
        }
        const attribs = [
            VertexBuffer.PositionKind,
            Map3dMaterial.TextureDepthsAttName,
            Map3dMaterial.ElevationUvsAttName,
            Map3dMaterial.ElevationDepthsAttName,
        ];
        const uniforms = [
            Map3dMaterial.ViewProjectionMatrixUniformName,
            Map3dMaterial.AltRangeUniformName,
            Map3dMaterial.MapScaleUniformName,
        ];
        const samplers = [Map3dMaterial.TextureSamplerUniformName, Map3dMaterial.ElevationSamplerUniformName];
        const uniformBuffers = new Array();
        if (this._holoBounds) {
            this._pushUniformsForBounds(defines, uniforms);
        }
        defines.INSTANCES = true;
        MaterialHelper.PushAttributesForInstances(attribs);
        defines.rebuild();
        const fallbacks = new EffectFallbacks();
        const engine = scene.getEngine();
        subMesh.setEffect(engine.createEffect(this._shaderName, {
            attributes: attribs,
            uniformsNames: uniforms,
            uniformBuffersNames: uniformBuffers,
            samplers: samplers,
            defines: defines.toString(),
            fallbacks: fallbacks,
            onCompiled: this._onEffectCompiled.bind(this),
            onError: this.onError,
        }, engine), defines, this._materialContext);
        if (!subMesh.effect || !subMesh.effect.isReady()) {
            return false;
        }
        defines._renderId = scene.getRenderId();
        drawWrapper._wasPreviouslyReady = true;
        drawWrapper._wasPreviouslyUsingInstances = !!useInstances;
        return true;
    }
    bindForSubMesh(world, mesh, subMesh) {
        const defines = subMesh.materialDefines;
        if (!defines) {
            return;
        }
        const effect = subMesh.effect;
        if (!effect) {
            return;
        }
        this._activeEffect = effect;
        const scene = this.getScene();
        this._bindMatrix(effect, world, scene);
        if (this._mustRebind(scene, effect, subMesh)) {
            this._bindClipPlanes(effect);
            this._bindSamplers(effect);
            this._bindElevations(effect);
        }
    }
    dispose(forceDisposeEffect) {
        super.dispose(forceDisposeEffect);
        this._textureSampler?.dispose();
        this._textureSampler = null;
        this._elevationSampler?.dispose();
        this._elevationSampler = null;
    }
    _reserveArea(sampler, mess) {
        if (!sampler) {
            return undefined;
        }
        let area = sampler.reserve();
        if (!area) {
            if (mess) {
                console.log(mess);
            }
            sampler.ensureRoomFor(1);
            area = sampler.reserve();
        }
        return area;
    }
    _ensureTextureSamplersReady(src) {
        if (!this._textureSampler) {
            let size = src.metrics.tileSize;
            this._textureSampler = this._buildTextureSampler(size, size);
            this._ensureInstanceBufferReady(src._map.grid);
        }
    }
    _ensureElevationSamplersReady(src) {
        if (!this._elevationSampler) {
            let size = src.metrics.tileSize;
            const offset = src.layer.zoomOffset ?? 0;
            const r = offset == 0 ? 1.0 : offset > 0 ? Math.pow(2, offset) : 1.0 / Math.pow(2, -offset);
            const depth = this._getOverallSamplerDepth(size * r);
            size++;
            this._elevationSampler = this._buildElevationSampler(size, size, depth);
        }
    }
    _ensureInstanceBufferReady(target) {
        if (target) {
            const mesh = target instanceof InstancedMesh ? target.sourceMesh : target;
            if (mesh.instancedBuffers?.depth === undefined) {
                this._registerInstanceBuffers(mesh);
            }
        }
    }
    _registerInstanceBuffers(target) {
        target.registerInstancedBuffer(Map3dMaterial.ElevationUvsAttName, Map3dMaterial.ElevationUvsSize);
        target.registerInstancedBuffer(Map3dMaterial.ElevationDepthsAttName, Map3dMaterial.ElevationDepthsSize);
        target.registerInstancedBuffer(Map3dMaterial.TextureDepthsAttName, Map3dMaterial.TextureDepthsSize);
    }
    _buildTextureSampler(width, height, depth) {
        height = height ?? width;
        const maxDepth = depth ?? this._getOverallSamplerDepth(width, height);
        const scene = this.getScene();
        const options = {
            width: width,
            height: height,
            depth: maxDepth,
            format: Constants.TEXTUREFORMAT_RGB,
            textureType: Constants.TEXTURETYPE_UNSIGNED_BYTE,
            samplingMode: Constants.TEXTURE_BILINEAR_SAMPLINGMODE,
            internalFormat: scene.getEngine()._gl.RGB8,
            generateMipMap: false,
        };
        return new Texture3(scene, options);
    }
    _buildElevationSampler(width, height, depth) {
        height = height ?? width;
        const maxDepth = depth ?? this._getOverallSamplerDepth(width, height);
        const scene = this.getScene();
        const options = {
            width: width,
            height: height,
            depth: maxDepth,
            format: Constants.TEXTUREFORMAT_R,
            textureType: Constants.TEXTURETYPE_FLOAT,
            samplingMode: Constants.TEXTURE_NEAREST_NEAREST,
            internalFormat: scene.getEngine()._gl.R16F,
            generateMipMap: false,
        };
        return new Texture3(scene, options);
    }
    _getOverallSamplerDepth(width, height) {
        if (Size3.IsEmpty(this._displayResolution)) {
            return Map3dMaterial.DefaultElevationTextureDepth;
        }
        height = height ?? width;
        const a = this._displayResolution.width / width;
        const b = this._displayResolution.height / height;
        const N = Math.ceil(a * b + 2 * (a + b));
        return N;
    }
    _bindMatrix(effect, world, scene) {
        effect.setMatrix(Map3dMaterial.ViewProjectionMatrixUniformName, scene.getTransformMatrix());
    }
    _bindElevations(effect) {
        const r = this._getElevationRange();
        effect.setVector2(Map3dMaterial.AltRangeUniformName, new Vector2(r.min, r.max));
        effect.setFloat(Map3dMaterial.MapScaleUniformName, this._mapScale.z);
    }
    _bindClipPlanes(effect) {
        if (this._holoBounds) {
            if (IsHolographicBox(this._holoBounds)) {
                const clips = this._holoBounds.clipPlanesWorld;
                if (clips) {
                    this._bindClipPlane(effect, clips, Map3dMaterial.NorthClipPlaneUniformName, ClipIndex.North);
                    this._bindClipPlane(effect, clips, Map3dMaterial.SouthClipPlaneUniformName, ClipIndex.South);
                    this._bindClipPlane(effect, clips, Map3dMaterial.EastClipPlaneUniformName, ClipIndex.East);
                    this._bindClipPlane(effect, clips, Map3dMaterial.WestClipPlaneUniformName, ClipIndex.West);
                }
            }
            else if (IsHolographicSphere(this._holoBounds)) {
                effect.setFloat(Map3dMaterial.RadiusUniformName, this._holoBounds.radius);
            }
            else if (IsHolographicCylinder(this._holoBounds)) {
                effect.setFloat(Map3dMaterial.RadiusUniformName, this._holoBounds.radius);
                effect.setVector3(Map3dMaterial.HeightUniformName, this._holoBounds.height ?? new Vector3(0, Number.MAX_SAFE_INTEGER, 0));
            }
        }
    }
    _bindClipPlane(effect, planes, name, index) {
        let clipPlane = planes[index];
        if (clipPlane) {
            effect.setVector3(`${name}.point`, clipPlane.point);
            effect.setVector3(`${name}.normal`, clipPlane.normal);
        }
    }
    _bindSamplers(effect) {
        effect.setTexture(Map3dMaterial.TextureSamplerUniformName, this._textureSampler);
        effect.setTexture(Map3dMaterial.ElevationSamplerUniformName, this._elevationSampler);
    }
    _bindHolographicBounds() {
        if (this._holoBounds) {
            if (IsHolographicBox(this._holoBounds)) {
                this._clipPlanesAddedObservers = this._holoBounds.clipPlanesAddedObservable.add(this._onClipPlanesAdded.bind(this));
                this._clipPlanesRemovedObservers = this._holoBounds.clipPlanesRemovedObservable.add(this._onClipPlanesRemoved.bind(this));
            }
        }
    }
    _unbindHolographicBounds() {
        if (this._holoBounds) {
            this._clipPlanesAddedObservers?.disconnect();
            this._clipPlanesRemovedObservers?.disconnect();
            this._clipPlanesAddedObservers = null;
            this._clipPlanesRemovedObservers = null;
            this.markAsDirty(Material.AttributesDirtyFlag);
        }
    }
    _pushUniformsForBounds(defines, uniforms) {
        if (IsHolographicBox(this._holoBounds)) {
            defines.HOLOGRAPHIC_BOUNDS_BOX = true;
            const properties = ["point", "normal"];
            uniforms.push(...this._prepareUniforms(Map3dMaterial.NorthClipPlaneUniformName, ...properties), ...this._prepareUniforms(Map3dMaterial.SouthClipPlaneUniformName, ...properties), ...this._prepareUniforms(Map3dMaterial.EastClipPlaneUniformName, ...properties), ...this._prepareUniforms(Map3dMaterial.WestClipPlaneUniformName, ...properties));
        }
        else if (IsHolographicSphere(this._holoBounds)) {
            throw new Error("Sphere bounds Not supported");
            defines.HOLOGRAPHIC_BOUNDS_SPHERE = true;
            uniforms.push(Map3dMaterial.RadiusUniformName);
        }
        else if (IsHolographicCylinder(this._holoBounds)) {
            throw new Error("Cylinder bounds Not supported");
            defines.HOLOGRAPHIC_BOUNDS_CYLINDER = true;
            uniforms.push(Map3dMaterial.RadiusUniformName, Map3dMaterial.HeightUniformName);
        }
    }
    _prepareUniforms(name, ...properties) {
        return properties.map((p) => `${name}.${p}`);
    }
    _onClipPlanesAdded(planes) {
        this.markAsDirty(Material.AttributesDirtyFlag);
    }
    _onClipPlanesRemoved(planes) {
        this.markAsDirty(Material.AttributesDirtyFlag);
    }
    _onEffectCompiled(effect) {
        if (this.onCompiled) {
            this.onCompiled(effect);
        }
    }
    _getElevationRange() {
        if (this._elevationRange === null) {
            this._elevationRange = this._buildElevationRange();
        }
        return this._elevationRange;
    }
    _invalidateElevationRange() {
        this._elevationRange = null;
    }
    _buildElevationRange() {
        let range = null;
        for (let b of this._elevationTileLayouts.values()) {
            const infos = b.tile.content;
            if (infos) {
                if (range === null) {
                    range = new Range(infos.min.z, infos.max.z);
                    continue;
                }
                range.unionInPlace(infos.min.z, infos.max.z);
            }
        }
        return range ?? new Range(Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER);
    }
    _updateElevationRange(infos) {
        if (infos) {
            this._getElevationRange().unionInPlace(infos.min.z, infos.max.z);
            this.markAsDirty(Material.AttributesDirtyFlag);
        }
    }
    _imagesAdded(data, state) {
        const host = state.currentTarget;
        if (host instanceof TextureLayerView) {
            for (const tile of data) {
                const key = tile.address.quadkey;
                if (this._textureTileLayouts.has(key)) {
                    continue;
                }
                const surface = tile.surface;
                if (!surface) {
                    throw new Error("Tile surface is not defined");
                }
                this._ensureTextureSamplersReady(host);
                let layout = new TextureLayout(tile, host);
                this._textureTileLayouts.set(key, layout);
                let area = this._reserveArea(this._textureSampler);
                if (area === undefined) {
                    return;
                }
                const textureDepths = new Vector4(area.depth, -1, -1, -1);
                surface.instancedBuffers[Map3dMaterial.TextureDepthsAttName] = textureDepths;
                const elevationUvs = new Vector4(-1, -1, -1, -1);
                surface.instancedBuffers[Map3dMaterial.ElevationUvsAttName] = elevationUvs;
                const elevationDepths = new Vector4(-1, -1, -1, -1);
                surface.instancedBuffers[Map3dMaterial.ElevationDepthsAttName] = elevationDepths;
                layout.area = area;
                this._ensureTileGeoBoundsIsReady(tile, host.metrics);
                if (tile.content) {
                    area.update(tile.content);
                    this._grabElevations(layout);
                    tile.surface?.setEnabled(true);
                }
                this.markAsDirty(Material.TextureDirtyFlag);
            }
        }
    }
    _imagesRemoved(data, state) {
        for (const tile of data) {
            const key = tile.address.quadkey;
            const layout = this._textureTileLayouts.get(key);
            if (layout) {
                if (tile.surface) {
                    tile.surface.instancedBuffers.textureDepths.x = -1;
                }
                layout.dispose();
                this._textureTileLayouts.delete(key);
                this.markAsDirty(Material.TextureDirtyFlag);
            }
        }
    }
    _imagesUpdated(data, state) {
        for (const tile of data) {
            const key = tile.address.quadkey;
            const layout = this._textureTileLayouts.get(key);
            if (layout) {
                if (tile.content) {
                    layout.area?.update(tile.content);
                    this._grabElevations(layout);
                    tile.surface?.setEnabled(true);
                    this.markAsDirty(Material.TextureDirtyFlag);
                }
            }
        }
    }
    _elevationsAdded(data, state) {
        const host = state.currentTarget;
        if (host instanceof ElevationLayerView) {
            for (const tile of data) {
                const key = tile.address.quadkey;
                if (this._elevationTileLayouts.has(key)) {
                    continue;
                }
                this._ensureElevationSamplersReady(host);
                let layout = new ElevationLayout(tile, host);
                this._elevationTileLayouts.set(key, layout);
                let area = this._reserveArea(this._elevationSampler);
                if (area === undefined) {
                    return;
                }
                layout.area = area;
                this._ensureTileGeoBoundsIsReady(tile, host.metrics);
                this._processElevations(layout, host);
                this.markAsDirty(Material.TextureDirtyFlag);
            }
        }
    }
    _elevationsRemoved(data, state) {
        for (const tile of data) {
            const key = tile.address.quadkey;
            const layout = this._elevationTileLayouts.get(key);
            if (layout) {
                layout.dispose();
                this._elevationTileLayouts.delete(key);
                this._invalidateElevationRange();
                this.markAsDirty(Material.TextureDirtyFlag);
            }
        }
    }
    _elevationsUpdated(data, state) {
        const host = state.currentTarget;
        if (host instanceof ElevationLayerView) {
            for (const tile of data) {
                const key = tile.address.quadkey;
                const layout = this._elevationTileLayouts.get(key);
                if (layout) {
                    this._processElevations(layout, host);
                }
            }
        }
    }
    _processElevations(layout, view) {
        const tile = layout.tile;
        if (tile.content?.elevations) {
            this._updateElevationRange(tile.content);
            const area = layout.area;
            if (area) {
                const size = view.metrics.tileSize;
                area.update(tile.content.elevations, 0, 0, size, size);
                let buffer = ElevationHelpers.GetLastColumn(tile.content.elevations, size, size);
                let z = buffer[size - 1];
                area.update(buffer, size, 0, 1, size);
                buffer = ElevationHelpers.GetLastRow(tile.content.elevations, size, size);
                z = (z + buffer[size - 1]) / 2;
                area.update(buffer, 0, size, size, 1);
                buffer = new Float32Array([z]);
                area.update(buffer, size, size, 1, 1);
            }
            this._updateNeighbourgs(layout);
            this._dispatchElevations(layout);
            this.markAsDirty(Material.TextureDirtyFlag);
        }
    }
    _grabElevations(layout) {
        const textureTile = layout.tile;
        const textureBounds = textureTile.geoBounds;
        const surface = textureTile.surface;
        if (textureBounds && surface) {
            const w1 = textureBounds.east - textureBounds.west;
            const h1 = textureBounds.north - textureBounds.south;
            for (const l of this._elevationTileLayouts.values()) {
                const elevationTile = l.tile;
                const elevationBounds = elevationTile.geoBounds;
                if (elevationBounds) {
                    if (elevationBounds.contains(textureBounds)) {
                        const w0 = elevationBounds.east - elevationBounds.west;
                        const h0 = elevationBounds.north - elevationBounds.south;
                        const elevationUvs = surface.instancedBuffers[Map3dMaterial.ElevationUvsAttName];
                        elevationUvs.x = (textureBounds.west - elevationBounds.west) / w0;
                        elevationUvs.y = -(textureBounds.north - elevationBounds.north) / h0;
                        elevationUvs.z = w1 / w0;
                        elevationUvs.w = h1 / h0;
                        const elevationDepths = surface.instancedBuffers[Map3dMaterial.ElevationDepthsAttName];
                        elevationDepths.x = elevationDepths.y = elevationDepths.z = elevationDepths.w = l.area?.depth ?? -1;
                    }
                }
            }
        }
    }
    _dispatchElevations(layout) {
        const elevationTile = layout.tile;
        const elevationBounds = elevationTile.geoBounds;
        if (elevationBounds) {
            const w0 = elevationBounds.east - elevationBounds.west;
            const h0 = elevationBounds.north - elevationBounds.south;
            for (const l of this._textureTileLayouts.values()) {
                const textureTile = l.tile;
                const textureBounds = textureTile.geoBounds;
                if (textureBounds) {
                    if (elevationBounds.contains(textureBounds)) {
                        const surface = textureTile.surface;
                        if (surface) {
                            const w1 = textureBounds.east - textureBounds.west;
                            const h1 = textureBounds.north - textureBounds.south;
                            const elevationUvs = surface.instancedBuffers[Map3dMaterial.ElevationUvsAttName];
                            elevationUvs.x = (textureBounds.west - elevationBounds.west) / w0;
                            elevationUvs.y = -(textureBounds.north - elevationBounds.north) / h0;
                            elevationUvs.z = w1 / w0;
                            elevationUvs.w = h1 / h0;
                            const elevationDepths = surface.instancedBuffers[Map3dMaterial.ElevationDepthsAttName];
                            elevationDepths.x = elevationDepths.y = elevationDepths.z = elevationDepths.w = layout.area?.depth ?? -1;
                        }
                    }
                }
            }
        }
    }
    _ensureTileGeoBoundsIsReady(tile, metrics) {
        if (tile.geoBounds == undefined || tile.geoBounds == null || tile.geoBounds.isEmpty()) {
            const env = Tile.BuildEnvelope(tile, metrics);
            tile.geoBounds = env;
        }
    }
    _updateNeighbourgs(layout) {
        const addresses = layout._neighbors;
        if (layout.tile.content?.elevations) {
            const elevations = layout.tile.content.elevations;
            const elevationTileSize = layout.layer.metrics.tileSize;
            let a = addresses[NeighborsAddress.NW];
            if (a) {
                const nwLayout = this._elevationTileLayouts.get(a.quadkey);
                if (nwLayout && nwLayout.area) {
                    const buffer = new Float32Array([elevations[0]]);
                    const size = nwLayout.layer.metrics.tileSize;
                    nwLayout.area.update(buffer, size, size, 1, 1);
                }
            }
            a = addresses[NeighborsAddress.N];
            if (a) {
                const nLayout = this._elevationTileLayouts.get(a.quadkey);
                if (nLayout && nLayout.area) {
                    const buffer = ElevationHelpers.GetFirstRow(elevations, elevationTileSize);
                    const size = nLayout.layer.metrics.tileSize;
                    nLayout.area.update(buffer, 0, size, size, 1);
                }
            }
            a = addresses[NeighborsAddress.E];
            if (a) {
                const eLayout = this._elevationTileLayouts.get(a.quadkey);
                const eElevations = eLayout?.tile.content?.elevations;
                if (eElevations && layout.area) {
                    const size = layout.layer.metrics.tileSize;
                    const buffer = ElevationHelpers.GetFirstColumn(eElevations, size, size);
                    layout.area.update(buffer, elevationTileSize, 0, 1, elevationTileSize);
                }
            }
            a = addresses[NeighborsAddress.SE];
            if (a) {
                const seLayout = this._elevationTileLayouts.get(a.quadkey);
                const seElevations = seLayout?.tile.content?.elevations;
                if (seElevations && layout.area) {
                    const buffer = new Float32Array([seElevations[0]]);
                    layout.area.update(buffer, elevationTileSize, elevationTileSize, 1, 1);
                }
            }
            a = addresses[NeighborsAddress.S];
            if (a) {
                const sLayout = this._elevationTileLayouts.get(a.quadkey);
                const sElevations = sLayout?.tile.content?.elevations;
                if (sElevations && layout.area) {
                    const size = layout.layer.metrics.tileSize;
                    const buffer = ElevationHelpers.GetFirstRow(sElevations, size);
                    layout.area.update(buffer, 0, elevationTileSize, elevationTileSize, 1);
                }
            }
            a = addresses[NeighborsAddress.W];
            if (a) {
                const wLayout = this._elevationTileLayouts.get(a.quadkey);
                if (wLayout?.area) {
                    const buffer = ElevationHelpers.GetFirstColumn(elevations, elevationTileSize, elevationTileSize);
                    const size = layout.layer.metrics.tileSize;
                    wLayout.area.update(buffer, size, 0, 1, size);
                }
            }
        }
    }
}
Map3dMaterial.ElevationDepthsEastProperty = "y";
Map3dMaterial.ElevationDepthsSouthProperty = "z";
Map3dMaterial.ElevationDepthsSouthEastProperty = "w";
Map3dMaterial.ClassName = "Map3dMaterial";
Map3dMaterial.ShaderName = "map";
Map3dMaterial.DefaultElevationTextureDepth = 16;
Map3dMaterial.ElevationUvsAttName = "elevationUvs";
Map3dMaterial.ElevationUvsSize = 4;
Map3dMaterial.ElevationDepthsAttName = "elevationDepths";
Map3dMaterial.ElevationDepthsSize = 4;
Map3dMaterial.TextureDepthsAttName = "textureDepths";
Map3dMaterial.TextureDepthsSize = 4;
Map3dMaterial.ViewProjectionMatrixUniformName = "viewProjection";
Map3dMaterial.NorthClipPlaneUniformName = "uNorthClip";
Map3dMaterial.SouthClipPlaneUniformName = "uSouthClip";
Map3dMaterial.EastClipPlaneUniformName = "uEastClip";
Map3dMaterial.WestClipPlaneUniformName = "uWestClip";
Map3dMaterial.RadiusUniformName = "uRadiusClip";
Map3dMaterial.HeightUniformName = "uHeightClip";
Map3dMaterial.AltRangeUniformName = "uAltRange";
Map3dMaterial.MapScaleUniformName = "uMapScale";
Map3dMaterial.TextureSamplerUniformName = "uTextures";
Map3dMaterial.ElevationSamplerUniformName = "uElevations";
//# sourceMappingURL=materials.map.js.map